import { useCallback, useRef, useState } from 'react'

export const useCountdown = ({ tenant }) => {
  const [remainingTime, setRemainingTime] = useState('')
  const [isUnderGap, setIsUnderGap] = useState(true)
  const [activated, setActivated] = useState(false)
  const expiryTimeRef = useRef(null)
  const gapTimeRef = useRef(null)
  const countdownIntervalRef = useRef(null)

  // format countdown time
  const countdown = useCallback((countdownseconds) => {
    const seconds = Math.floor(countdownseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    const formattedDays = days < 10 ? `0${days}` : days
    const formattedHours = hours % 24 < 10 ? `0${hours % 24}` : hours % 24
    const formattedMinutes =
      minutes % 60 < 10 ? `0${minutes % 60}` : minutes % 60
    const formattedSeconds =
      seconds % 60 < 10 ? `0${seconds % 60}` : seconds % 60

    const remaining = `${formattedDays}d:${formattedHours}h:${formattedMinutes}m:${formattedSeconds}s`
    setRemainingTime(remaining)
  }, [])

  // update countdown state
  const updateCountdown = useCallback(() => {
    if (!expiryTimeRef.current) {
      return
    }

    const expiry = expiryTimeRef.current
    const now = new Date()
    const diffMs = expiry.getTime() - now.getTime()

    if (diffMs <= 0) {
      setActivated(false)

      // calculate gap time from expiry moment
      const activationGapMinutes =
        tenant?.tenantmasterconfig?.time_bound.activation_gap || 0
      const newGapTime = new Date(
        expiry.getTime() + activationGapMinutes * 60 * 1000
      )
      gapTimeRef.current = newGapTime

      const gapdiff = gapTimeRef.current.getTime() - now.getTime()

      if (gapdiff > 0) {
        setIsUnderGap(true)
        countdown(gapdiff)
      } else {
        setIsUnderGap(false)
        setRemainingTime('')
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    } else {
      countdown(diffMs)
      setActivated(true)
      setIsUnderGap(false)
    }
  }, [countdown, tenant?.tenantmasterconfig?.time_bound.activation_gap])

  // start countdown
  const startCountdown = useCallback(
    (exp, isReactivation = false) => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }

      const expiry = new Date(exp)
      expiryTimeRef.current = expiry

      // only set initial gap time if it's a reactivation
      if (isReactivation) {
        const activationGapMinutes =
          tenant?.tenantmasterconfig?.time_bound.activation_gap || 0
        const gapTime = new Date(
          expiry.getTime() + activationGapMinutes * 60 * 1000
        )
        gapTimeRef.current = gapTime
      }

      updateCountdown()
      countdownIntervalRef.current = setInterval(updateCountdown, 1000)
    },
    [tenant, updateCountdown]
  )

  // cleanup function
  const cleanup = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
  }, [])

  return {
    remainingTime,
    isUnderGap,
    activated,
    startCountdown,
    setActivated,
    cleanup,
    setIsUnderGap,
  }
}
